import React, { useState } from "react";
import {
  LogOut,
  List,
  Clock,
  ShoppingCart,
  FileText,
  Layers,
  Search,
  Eye,
  Check,
  Plus,
  Send,
  Play,
} from "lucide-react";
import "./pmdashboard.css";
import { useNavigate } from "react-router-dom";
import ScreenHeader from "../../components/ScreenHeader";
import BackButton from "../../components/backbutton";
import Pagination from "../../components/Pagination";
import RootSheet from "../../components/RootSheet";

export default function ProductionManagerDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("list-schedule");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSubAssemblyModal, setShowSubAssemblyModal] = useState(false);
  const [showRootSheetModal, setShowRootSheetModal] = useState(false);
  const [currentSubAssemblyStep, setCurrentSubAssemblyStep] = useState(1);
  const navigate = useNavigate();

  // Pagination States
  const itemsPerPage = 5;
  const [currentSchedulePage, setCurrentSchedulePage] = useState(1);
  const [currentSaleOrderPage, setCurrentSaleOrderPage] = useState(1);
  // const [currentProcessOutputPage, setCurrentProcessOutputPage] = useState(1);

  const resetPagination = () => {
    setCurrentSchedulePage(1);
    setCurrentSaleOrderPage(1);
  };

  // Sample Schedule Data
  const [schedules] = useState([
    { id: "SCH-001", customer: "ABC Corp", product: "Product A", quantity: 100, timeLimit: "2024-04-20", status: "Pending", priority: "High" },
    { id: "SCH-002", customer: "XYZ Ltd", product: "Product B", quantity: 50, timeLimit: "2024-04-22", status: "Pending", priority: "Medium" },
    { id: "SCH-003", customer: "PQR Industries", product: "Product C", quantity: 75, timeLimit: "2024-04-25", status: "Approved", priority: "Low" },
    { id: "SCH-004", customer: "LMN Corp", product: "Product D", quantity: 120, timeLimit: "2024-04-28", status: "Pending", priority: "High" },
    { id: "SCH-005", customer: "DEF Ltd", product: "Product E", quantity: 90, timeLimit: "2024-05-01", status: "Pending", priority: "Medium" },
  ]);

  // Stock Availability Data
  const [stockData] = useState([
    { product: "Product A", available: 100, required: 60, status: "Available" },
    { product: "Product B", available: 40, required: 50, status: "Shortage" },
    { product: "Product C", available: 0, required: 30, status: "No Stock" },
    { product: "Product D", available: 80, required: 60, status: "Available" },
  ]);

  // Sample Sale Orders
  const [saleOrders] = useState([
    { id: "SO-001", orderNumber: "SO-2024-001", customer: "ABC Corp", product: "Product A", quantity: 100, amount: "₹1,50,000", date: "2024-03-15", status: "Pending" },
    { id: "SO-002", orderNumber: "SO-2024-002", customer: "XYZ Ltd", product: "Product B", quantity: 50, amount: "₹85,000", date: "2024-03-14", status: "Pending" },
    { id: "SO-003", orderNumber: "SO-2024-003", customer: "PQR Industries", product: "Product C", quantity: 75, amount: "₹1,20,000", date: "2024-03-13", status: "Pending" },
    { id: "SO-004", orderNumber: "SO-2024-004", customer: "LMN Corp", product: "Product D", quantity: 120, amount: "₹1,80,000", date: "2024-03-12", status: "Approved" },
  ]);

  // Sample Supplied BOM
  const [suppliedBOM] = useState([
    { id: "BOM-001", product: "Product A", components: [
      { name: "Component A1", quantity: 2, supplier: "ABC Suppliers" },
      { name: "Component A2", quantity: 1, supplier: "XYZ Enterprises" }
    ]},
    { id: "BOM-002", product: "Product B", components: [
      { name: "Component B1", quantity: 3, supplier: "Tech Solutions" }
    ]},
    { id: "BOM-003", product: "Product C", components: [
      { name: "Component C1", quantity: 1, supplier: "Global Parts" },
      { name: "Component C2", quantity: 4, supplier: "Prime Materials" }
    ]},
  ]);

  // Sample Process Output
  const [processOutputs] = useState([
    { id: "PO-001", product: "Product A", process: "Cutting", quantityAccepted: 95, quantityRejected: 3, quantityNC: 1, scrap: 1, rework: 0, date: "2024-03-20" },
    { id: "PO-002", product: "Product A", process: "Assembly", quantityAccepted: 92, quantityRejected: 5, quantityNC: 2, scrap: 1, rework: 2, date: "2024-03-21" },
  ]);

  // Button styles
  const buttonStyles = {
    view: { backgroundColor: '#E0E7FF', color: '#4F46E5' },
    approve: { backgroundColor: '#D1FAE5', color: '#059669' },
    reject: { backgroundColor: '#FEE2E2', color: '#DC2626' },
    alert: { backgroundColor: '#FEF3C7', color: '#D97706' },
  };

  // Action Button Component
  const ActionButton = ({ onClick, type, icon: Icon, tooltip }) => {
    const style = buttonStyles[type];
    return (
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          onClick={onClick}
          style={{
            padding: '6px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: style.backgroundColor,
            color: style.color,
            transition: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = style.backgroundColor;
            e.currentTarget.style.color = style.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = style.backgroundColor;
            e.currentTarget.style.color = style.color;
          }}
        >
          <Icon size={16} />
        </button>
        <span className="tooltip-span">{tooltip}</span>
      </div>
    );
  };

  // Sidebar menu items - Only the 5 required sections
  const tabItems = [
    { id: "list-schedule", label: "List Schedule", icon: <List size={18} /> },
    { id: "pending-production", label: "Pending Production Plan", icon: <Clock size={18} /> },
    { id: "root-sheet", label: "Root Sheet", icon: <FileText size={18} /> },
    { id: "list-sale-order", label: "List Sale Order", icon: <ShoppingCart size={18} /> },
    { id: "supplied-bom", label: "Supplied BOM", icon: <FileText size={18} /> },
    { id: "last-process", label: "Last Process", icon: <Layers size={18} /> },
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      searchFields.some(field => 
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // ============================================
  // 1. LIST SCHEDULE
  // ============================================
  const ListScheduleContent = () => {
    const filteredSchedules = filteredData(schedules, ['id', 'customer', 'product']);
    const totalPages = Math.ceil(filteredSchedules.length / itemsPerPage);
    const currentSchedules = filteredSchedules.slice(
      (currentSchedulePage - 1) * itemsPerPage, 
      currentSchedulePage * itemsPerPage
    );

    return (
      <div className="content-wrapper">
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search schedules..." className="search-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); resetPagination(); }} />
        </div>
        <div className="table-responsive">
          <table className="pm-table">
            <thead>
              <tr>
                <th>Schedule ID</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Time Limit</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentSchedules.map(sch => (
                <tr key={sch.id}>
                  <td className="highlight">{sch.id}</td>
                  <td>{sch.customer}</td>
                  <td>{sch.product}</td>
                  <td>{sch.quantity}</td>
                  <td>{sch.timeLimit}</td>
                  <td><span className={`badge ${sch.priority === 'High' ? 'badge-warning' : sch.priority === 'Medium' ? 'badge-info' : 'badge-success'}`}>{sch.priority}</span></td>
                  <td><span className={`badge ${sch.status === 'Approved' ? 'badge-success' : 'badge-warning'}`}>{sch.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <ActionButton onClick={() => alert(`Schedule: ${sch.customer}\nProduct: ${sch.product}\nQty: ${sch.quantity}`)} type="view" icon={Eye} tooltip="View" />
                      {sch.status === 'Pending' && (
                        <ActionButton onClick={() => alert(`Schedule ${sch.id} Approved!`)} type="approve" icon={Check} tooltip="Approve" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination currentPage={currentSchedulePage} totalPages={totalPages} onPageChange={setCurrentSchedulePage} pageSize={itemsPerPage} totalItems={filteredSchedules.length} />
        </div>
      </div>
    );
  };

  // ============================================
  // 2. PENDING PRODUCTION PLAN (Includes Stock, Acceptance, Root Sheet, Product Process, Process Output)
  // ============================================
  const PendingProductionContent = () => {
    const [selectedProduct, setSelectedProduct] = useState("");
    const [selectedProcess, setSelectedProcess] = useState("");
    const [productQuantity, setProductQuantity] = useState("");
    const [outputData, setOutputData] = useState({ accepted: '', rejected: '', nc: '', scrap: '', rework: '' });

    const [productionDecision, setProductionDecision] = useState("");

    const handleStartProduction = () => {
      if (!selectedProduct) {
        alert("Please select a product");
        return;
      }
      alert(`Production started for ${selectedProduct}!`);
    };

    return (
      <div className="content-wrapper">
        {/* Stock Availability Section */}
        <div className="section-card">
          <h3 className="section-title">Stock Availability</h3>
          <div className="table-responsive">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Available</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {stockData.map((item, idx) => (
                  <tr key={idx}>
                    <td className="highlight">{item.product}</td>
                    <td>{item.available}</td>
                    <td>{item.required}</td>
                    <td>
                      <span className={`badge ${item.status === 'Available' ? 'badge-success' : item.status === 'Shortage' ? 'badge-warning' : 'badge-danger'}`}>
                        {item.status === 'Shortage' ? `Shortage (${item.required - item.available})` : item.status}
                      </span>
                    </td>
                    <td>
                      {item.status === 'Available' && (
                        <ActionButton onClick={() => alert(`Accepting production for ${item.product}`)} type="approve" icon={Check} tooltip="Accept" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Production Decision */}
        <div className="section-card">
          <h3 className="section-title">Production Decision</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Select Product</label>
              <select className="form-input" value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
                <option value="">-- Select Product --</option>
                <option value="Product A">Product A</option>
                <option value="Product B">Product B</option>
                <option value="Product C">Product C</option>
              </select>
            </div>
            <div className="form-group">
              <label>Production Type</label>
              <select className="form-input" value={productionDecision} onChange={(e) => setProductionDecision(e.target.value)}>
                <option value="">-- Select --</option>
                <option value="outsource">Outsource to Vendor</option>
                <option value="inhouse">Inhouse Production</option>
              </select>
            </div>
          </div>
          {productionDecision === 'inhouse' && (
            <div className="section-card inner-card">
              <div className="form-row">
                <div className="form-group">
                  <label>Select Product</label>
                  <select className="form-input" value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
                    <option value="">-- Select --</option>
                    <option value="Product A">Product A</option>
                    <option value="Product B">Product B</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Process</label>
                  <select className="form-input" value={selectedProcess} onChange={(e) => setSelectedProcess(e.target.value)}>
                    <option value="">-- Select --</option>
                    <option value="Cutting">Cutting</option>
                    <option value="Assembly">Assembly</option>
                    <option value="Molding">Molding</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Product Quantity</label>
                  <input type="number" className="form-input" placeholder="Enter quantity" value={productQuantity} onChange={(e) => setProductQuantity(e.target.value)} />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="btn-primary" onClick={handleStartProduction}><Play size={14} /> Start Production</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Root Sheet Section */}
        <div className="section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <h3 className="section-title" style={{ margin: 0 }}>Production Root Sheet</h3>
            <button className="btn-add" onClick={() => setShowRootSheetModal(true)} style={{ width: 'auto' }}>
              <Plus size={14} /> Open Root Sheet
            </button>
          </div>
          <div style={{ marginTop: '12px', color: '#64748B', fontSize: '13px' }}>
            Use the menu item for the full page, or open the same root sheet in a modal from here.
          </div>
        </div>

        {/* Product Process Output Section */}
        <div className="section-card">
          <h3 className="section-title">Product Process Output</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Select Product</label>
              <select className="form-input">
                <option value="">-- Select --</option>
                <option>Product A</option>
                <option>Product B</option>
              </select>
            </div>
            <div className="form-group">
              <label>Select Process</label>
              <select className="form-input">
                <option value="">-- Select --</option>
                <option>Cutting</option>
                <option>Assembly</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Quantity Accepted</label><input type="number" className="form-input" placeholder="Complete" value={outputData.accepted} onChange={(e) => setOutputData({...outputData, accepted: e.target.value})} /></div>
            <div className="form-group"><label>Quantity Rejected</label><input type="number" className="form-input" placeholder="Rejected" value={outputData.rejected} onChange={(e) => setOutputData({...outputData, rejected: e.target.value})} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Quantity NC</label><input type="number" className="form-input" placeholder="Non-Conforming" value={outputData.nc} onChange={(e) => setOutputData({...outputData, nc: e.target.value})} /></div>
            <div className="form-group"><label>Scrap</label><input type="number" className="form-input" placeholder="Scrap" value={outputData.scrap} onChange={(e) => setOutputData({...outputData, scrap: e.target.value})} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Rework</label><input type="number" className="form-input" placeholder="Rework" value={outputData.rework} onChange={(e) => setOutputData({...outputData, rework: e.target.value})} /></div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn-primary" onClick={() => alert("Process output recorded!")}><Check size={14} /> Complete Process</button>
            </div>
          </div>
          <div className="table-responsive" style={{ marginTop: '16px' }}>
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Date</th><th>Product</th><th>Process</th><th>Accepted</th><th>Rejected</th><th>NC</th><th>Scrap</th><th>Rework</th>
                </tr>
              </thead>
              <tbody>
                {processOutputs.map(po => (
                  <tr key={po.id}>
                    <td>{po.date}</td><td>{po.product}</td><td>{po.process}</td>
                    <td className="text-success">{po.quantityAccepted}</td>
                    <td className="text-danger">{po.quantityRejected}</td>
                    <td className="text-warning">{po.quantityNC}</td>
                    <td className="text-warning">{po.scrap}</td>
                    <td className="text-info">{po.rework}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // 3. LIST SALE ORDER
  // ============================================
  const ListSaleOrderContent = () => {
    const filteredOrders = filteredData(saleOrders, ['orderNumber', 'customer', 'product']);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const currentOrders = filteredOrders.slice(
      (currentSaleOrderPage - 1) * itemsPerPage, 
      currentSaleOrderPage * itemsPerPage
    );

    return (
      <div className="content-wrapper">
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search sale orders..." className="search-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); resetPagination(); }} />
        </div>
        <div className="table-responsive">
          <table className="pm-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map(order => (
                <tr key={order.id}>
                  <td className="highlight">{order.orderNumber}</td>
                  <td>{order.customer}</td>
                  <td>{order.product}</td>
                  <td>{order.quantity}</td>
                  <td className="highlight">{order.amount}</td>
                  <td>{order.date}</td>
                  <td><span className={`badge ${order.status === 'Approved' ? 'badge-success' : 'badge-warning'}`}>{order.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <ActionButton onClick={() => alert(`Order ${order.orderNumber}\nCustomer: ${order.customer}\nProduct: ${order.product}`)} type="view" icon={Eye} tooltip="View" />
                      {order.status === 'Pending' && (
                        <ActionButton onClick={() => alert(`Sale Order ${order.id} Approved!`)} type="approve" icon={Check} tooltip="Approve" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination currentPage={currentSaleOrderPage} totalPages={totalPages} onPageChange={setCurrentSaleOrderPage} pageSize={itemsPerPage} totalItems={filteredOrders.length} />
        </div>
      </div>
    );
  };

  // ============================================
  // 4. SUPPLIED BOM
  // ============================================
  const SuppliedBOMContent = () => (
    <div className="content-wrapper">
      <div className="bom-grid">
        {suppliedBOM.map(bom => (
          <div key={bom.id} className="bom-card">
            <div className="bom-card-header">
              <h3>{bom.product}</h3>
            </div>
            <div className="table-responsive" style={{ overflowX: 'visible' }}>
              <table className="pm-table" style={{ minWidth: 'auto', border: 'none' }}>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Quantity</th>
                    <th>Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  {bom.components.map((comp, idx) => (
                    <tr key={idx}>
                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{comp.name}</td>
                      <td>{comp.quantity}</td>
                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{comp.supplier}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ============================================
  // 5. LAST PROCESS
  // ============================================
  const LastProcessContent = () => (
    <div className="content-wrapper">
      <div className="section-card">
        <div className="form-row">
          <div className="form-group">
            <label>Select Product</label>
            <select className="form-input">
              <option value="">-- Select Product --</option>
              <option>Product A</option>
              <option>Product B</option>
              <option>Product C</option>
            </select>
          </div>
        </div>

        <div className="section-card inner-card">
          <h4 className="subsection-title">Move to:</h4>
          <div className="form-row">
            <label className="radio-label"><input type="radio" name="moveTo" /> Sub Assembly</label>
            <label className="radio-label"><input type="radio" name="moveTo" /> Finished Goods</label>
          </div>
        </div>

        <div className="section-card inner-card">
          <h4 className="subsection-title">Select Resources</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Employee</label>
              <select className="form-input">
                <option value="">Select Employee</option>
                <option>John Doe</option>
                <option>Jane Smith</option>
              </select>
            </div>
            <div className="form-group">
              <label>Machine</label>
              <select className="form-input">
                <option value="">Select Machine</option>
                <option>Assembly Line</option>
                <option>Packaging Machine</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Tool</label>
              <select className="form-input">
                <option value="">Select Tool</option>
                <option>Wrench</option>
                <option>Screwdriver</option>
              </select>
            </div>
            <div className="form-group">
              <label>Instrument</label>
              <select className="form-input">
                <option value="">Select Instrument</option>
                <option>Caliper</option>
                <option>Gauge</option>
              </select>
            </div>
          </div>
        </div>

        <div className="section-card inner-card">
          <h4 className="subsection-title">Accessories Checklist</h4>
          <div className="form-row">
            <label className="checkbox-label"><input type="checkbox" /> No Damage</label>
            <label className="checkbox-label"><input type="checkbox" /> No Bent</label>
          </div>
        </div>

        <div className="section-card inner-card">
          <h4 className="subsection-title">Data</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Weight Loss</label>
              <input type="text" className="form-input" placeholder="Weight Loss" />
            </div>
            <div className="form-group">
              <label>Accepted Quantity</label>
              <input type="text" className="form-input" placeholder="Accepted" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Missed Quantity</label>
              <input type="text" className="form-input" placeholder="Missed" />
            </div>
          </div>
        </div>

        <div className="form-row" style={{ justifyContent: 'space-between', marginTop: '16px' }}>
          <button className="btn-add" onClick={() => setShowSubAssemblyModal(true)}>
            <Plus size={14} /> Add Assembly Process
          </button>
          <button className="btn-primary" onClick={() => alert("Last process completed!")}>
            <Send size={14} /> Submit
          </button>
        </div>
      </div>

      {/* Sub Assembly Modal */}
      {showSubAssemblyModal && (
        <div className="modal-overlay" onClick={() => setShowSubAssemblyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sub Assembly Process ({currentSubAssemblyStep}/4)</h2>
              <button className="modal-close" onClick={() => setShowSubAssemblyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {currentSubAssemblyStep === 1 && (
                <div className="form-column">
                  <div className="form-group"><label>Product</label><input type="text" className="form-input" placeholder="Product Name" /></div>
                  <div className="form-group"><label>FG</label><input type="text" className="form-input" placeholder="Finished Good" /></div>
                  <div className="form-group"><label>Process Name</label><input type="text" className="form-input" placeholder="Process Name" /></div>
                  <div className="form-group"><label>New Product Name</label><input type="text" className="form-input" placeholder="New Product" /></div>
                </div>
              )}
              {currentSubAssemblyStep === 2 && (
                <div className="form-column">
                  <div className="form-group"><label>Process ID</label><input type="text" className="form-input" placeholder="Process ID" /></div>
                  <div className="form-group"><label>Process Detail</label><textarea className="form-textarea" rows="3" placeholder="Process Details..."></textarea></div>
                </div>
              )}
              {currentSubAssemblyStep === 3 && (
                <div className="form-column">
                  <label className="checkbox-label"><input type="checkbox" /> Checklist Item 1</label>
                  <label className="checkbox-label"><input type="checkbox" /> Checklist Item 2</label>
                  <label className="checkbox-label"><input type="checkbox" /> Checklist Item 3</label>
                  <div className="form-group"><label>Data</label><input type="text" className="form-input" placeholder="Additional Data" /></div>
                </div>
              )}
              {currentSubAssemblyStep === 4 && (
                <div className="form-column">
                  <div className="form-row">
                    <div className="form-group"><label>Accepted</label><input type="number" className="form-input" placeholder="Accepted" /></div>
                    <div className="form-group"><label>NC</label><input type="number" className="form-input" placeholder="NC" /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Rejected</label><input type="number" className="form-input" placeholder="Rejected" /></div>
                    <div className="form-group"><label>Scrap</label><input type="number" className="form-input" placeholder="Scrap" /></div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {currentSubAssemblyStep > 1 && (
                <button className="btn-outline" onClick={() => setCurrentSubAssemblyStep(currentSubAssemblyStep - 1)}>Previous</button>
              )}
              {currentSubAssemblyStep < 4 ? (
                <button className="btn-primary" onClick={() => setCurrentSubAssemblyStep(currentSubAssemblyStep + 1)}>Next</button>
              ) : (
                <button className="btn-primary" onClick={() => { alert("Sub Assembly Process Completed!"); setShowSubAssemblyModal(false); setCurrentSubAssemblyStep(1); }}>Complete Process</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "list-schedule": return <ListScheduleContent />;
      case "pending-production": return <PendingProductionContent />;
      case "root-sheet": return <RootSheet />;
      case "list-sale-order": return <ListSaleOrderContent />;
      case "supplied-bom": return <SuppliedBOMContent />;
      case "last-process": return <LastProcessContent />;
      default: return <div className="content-wrapper"><h2>Select a menu item</h2></div>;
    }
  };

  return (
    <div className="pm-dashboard">
      {/* Sidebar */}
      <div className="pm-sidebar">
        <div className="pm-logo">
          <div className="pm-logo-icon"><img src="/rahulerp.jpg" alt="Logo" className="pm-logo-img" /></div>
          <div><h2 className="pm-brand">Ragul Auto Tech</h2><p className="pm-tagline">AI Powered ERP</p></div>
        </div>
        <nav className="pm-nav">
          {tabItems.map((tab) => (
            <button key={tab.id} className={`pm-nav-item ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="pm-nav-icon">{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="pm-logout-wrap">
          <button onClick={handleLogoutClick} className="pm-logout-btn"><LogOut size={18} /> Logout</button>
        </div>
      </div>

      {/* Main */}
      <div className="pm-main">
        <ScreenHeader title="Production Manager Dashboard" description={`Welcome, ${user?.role || 'Production Manager'}`} timestamp={new Date().toLocaleString()} containerClassName="pm-header" titleClassName="pm-title" descriptionClassName="pm-subtitle" />
        <div className="pm-back-wrap"><BackButton /></div>
        <div className="pm-scroll">{renderContent()}</div>
      </div>

      {showRootSheetModal && (
        <div className="modal-overlay" onClick={() => setShowRootSheetModal(false)}>
          <div className="modal-content" style={{ maxWidth: '960px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Production Root Sheet</h2>
              <button className="modal-close" onClick={() => setShowRootSheetModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <RootSheet variant="modal" />
            </div>
          </div>
        </div>
      )}

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-modal-header">
              <div className="logout-modal-icon">👋</div>
              <h3>Confirm Logout</h3>
            </div>
            <div className="logout-modal-body">
              <p>Are you sure you want to logout from <strong>Production Manager</strong> dashboard?</p>
              <p className="logout-modal-warning">You will be redirected to the login page.</p>
            </div>
            <div className="logout-modal-footer">
              <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
              <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}